import {
	ONBOARDING_CODE_TO_SERVICE_ACCESS_TEMPLATE,
	OnboardingCode,
	SERVICE_ACCESS_TEMPLATE_CODES,
} from '@ekhodealer/ekho-common';
import { ContentWithTooltip, Dropdown, type DropdownOptionProps } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

/** Human-readable labels for service access template codes */
const TEMPLATE_DISPLAY_NAMES: Record<string, string> = {
	[SERVICE_ACCESS_TEMPLATE_CODES.DEFAULT]: 'Default',
	[SERVICE_ACCESS_TEMPLATE_CODES.ACCESS_PROGRAM_OWNER]: 'Access Program Owner',
	[SERVICE_ACCESS_TEMPLATE_CODES.OMNI_PROGRAM_OWNER]: 'Omni Program Owner',
	[SERVICE_ACCESS_TEMPLATE_CODES.DEALER_PROGRAM_OWNER]: 'Dealer Program Owner',
	[SERVICE_ACCESS_TEMPLATE_CODES.DEALER_SOLO_ROOFTOP]: 'Dealer Solo Rooftop',
	[SERVICE_ACCESS_TEMPLATE_CODES.OMNI_ROOFTOP]: 'Omni Rooftop',
	[SERVICE_ACCESS_TEMPLATE_CODES.DEALER_ROOFTOP]: 'Dealer Rooftop',
	[SERVICE_ACCESS_TEMPLATE_CODES.PICKUP_LOCATION]: 'Pickup Location',
	[SERVICE_ACCESS_TEMPLATE_CODES.EKHO_FULL_ACCESS]: 'Ekho Full Access',
};

const TOOLTIP_TEXT =
	'Your program defines which Ekho services and settings are available to your dealership. ' +
	'If your inventory is listed on multiple storefronts — for example, your own website and a manufacturer or dealer group site — ' +
	'each storefront may operate under a different program with its own checkout, fulfillment, and operational settings. ' +
	'Programs are managed by Ekho. Contact your account manager if you have questions or need changes.';

export function ProgramSelector(): React.ReactNode {
	const { data: servicesProvider } = useCurrentServicesProvider();

	// Resolve template: direct serviceAccess.template, or derive from onboardingCode
	let template = servicesProvider?.serviceAccess?.template;
	if (!template && servicesProvider?.onboardingCode) {
		const code = servicesProvider.onboardingCode;
		if (code in OnboardingCode) {
			template = ONBOARDING_CODE_TO_SERVICE_ACCESS_TEMPLATE[code as OnboardingCode];
		}
	}

	const templateOptions: DropdownOptionProps[] = Object.entries(TEMPLATE_DISPLAY_NAMES).map(
		([id, label]) => ({ label, id }),
	);

	// When disabled (single program), show the dealership name as a cosmetic label
	const isDisabled = true;
	const dealerName = servicesProvider?.name ?? 'Dealership';

	const options: DropdownOptionProps[] = isDisabled
		? [{ label: dealerName, id: template ?? SERVICE_ACCESS_TEMPLATE_CODES.DEFAULT }]
		: templateOptions;

	return (
		<ContentWithTooltip
			content={
				<Dropdown
					options={options}
					value={template ?? SERVICE_ACCESS_TEMPLATE_CODES.DEFAULT}
					size='small'
					buttonVariant='outline'
					disabled={isDisabled}
				/>
			}
			tooltipContent={TOOLTIP_TEXT}
		/>
	);
}
